import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ShieldCheck, Loader2, Mail, CheckCircle2, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { LOGO_URL } from '../lib/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL = {
  name: '', email: '', company_name: '', jurisdiction: '', phone: '',
};

const RequestAssessment = () => {
  const [searchParams] = useSearchParams();
  const source = useMemo(() => searchParams.get('source') || 'direct', [searchParams]);

  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company_name || !form.jurisdiction) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/public/request-assessment`, { ...form, source });
      setSubmitted(true);
      setResent(Boolean(r.data?.resent));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-[#0F172A] flex items-center justify-center p-4">
        <Card className="bg-slate-800/80 border-slate-700 max-w-lg w-full backdrop-blur" data-testid="request-success">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-[#10B981]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">
              {resent ? "We've resent your assessment link" : "Your assessment is on its way"}
            </h1>
            <p className="text-slate-300 mb-2">
              Check your inbox at <span className="text-[#10B981] font-medium">{form.email}</span> in
              the next few minutes for a link to start your data protection compliance assessment.
            </p>
            <p className="text-slate-500 text-sm mb-6">
              Can't find it? Check your spam folder or contact us at{' '}
              <a href="mailto:info@myprivicy.com" className="text-[#3B82F6] underline">info@myprivicy.com</a>.
            </p>
            <Button
              variant="outline"
              onClick={() => { setSubmitted(false); setForm(INITIAL); }}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Submit another request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-[#0F172A]" data-testid="request-assessment-page">
      <div className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left — value prop */}
        <div className="text-white">
          <img src={LOGO_URL} alt="Privacy Pulse" className="h-10 mb-6" />
          <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-4 font-['Manrope']">
            Where does your data protection compliance actually stand?
          </h1>
          <p className="text-slate-300 text-lg mb-6">
            A confidential, 20-minute assessment mapped to your jurisdiction's data
            protection law. We deliver a personalised compliance report with a
            prioritised action plan within a few business days.
          </p>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span>Covers Jamaica, Barbados, Bermuda, Cayman, The Bahamas, Belize and Grenada</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span>13 critical compliance areas — governance, breach response, vendor management, subject rights, and more</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span>Free to start. No credit card. No login required.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span>Confidential — your responses are reviewed only by Privicy Advisory Services</span>
            </li>
          </ul>
        </div>

        {/* Right — form */}
        <Card className="bg-slate-800/80 border-slate-700 backdrop-blur">
          <CardContent className="p-6 lg:p-8">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-5 w-5 text-[#10B981]" />
              <h2 className="text-xl font-bold text-white">Request your assessment link</h2>
            </div>
            <p className="text-slate-400 text-sm mb-6">
              Fill in your details and we'll email you a private link to begin.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-slate-300">Your Name *</Label>
                <Input
                  data-testid="request-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  required
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Company *</Label>
                <Input
                  data-testid="request-company"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Acme Corp"
                  required
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Work Email *</Label>
                <Input
                  type="email"
                  data-testid="request-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@acme.com"
                  required
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Jurisdiction *</Label>
                <Select
                  value={form.jurisdiction}
                  onValueChange={(v) => setForm({ ...form, jurisdiction: v })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="request-jurisdiction">
                    <SelectValue placeholder="Where does your organisation operate?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jamaica">🇯🇲 Jamaica</SelectItem>
                    <SelectItem value="barbados">🇧🇧 Barbados</SelectItem>
                    <SelectItem value="bermuda">🇧🇲 Bermuda</SelectItem>
                    <SelectItem value="cayman">🇰🇾 Cayman Islands</SelectItem>
                    <SelectItem value="bahamas">🇧🇸 The Bahamas</SelectItem>
                    <SelectItem value="belize">🇧🇿 Belize</SelectItem>
                    <SelectItem value="grenada">🇬🇩 Grenada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Phone (optional)</Label>
                <Input
                  data-testid="request-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1-868-555-1234"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                />
              </div>

              <p className="text-xs text-slate-500 leading-relaxed">
                By submitting this form, you agree to be contacted by Privicy Advisory Services about
                your assessment. We handle your information in line with our{' '}
                <a
                  href="https://myprivicy.com/privacy#assessment-notice"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#3B82F6] underline"
                >
                  Privacy Notice for Assessments
                </a>.
              </p>

              <Button
                type="submit"
                data-testid="request-submit"
                disabled={submitting}
                className="w-full bg-[#10B981] hover:bg-[#059669] text-white text-base h-11"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Email me my assessment link
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="text-center py-6 text-slate-500 text-xs border-t border-slate-800 mt-12">
        © {new Date().getFullYear()} Privicy Advisory Services. Email{' '}
        <a href="mailto:info@myprivicy.com" className="text-slate-400 hover:text-slate-200">info@myprivicy.com</a>
        {' '}· Phone 1-868-461-4572
      </footer>
    </div>
  );
};

export default RequestAssessment;
